import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { DashboardKpiResponse } from '../../types';

interface DashboardKpiProps {
  kpiMetrics: DashboardKpiResponse;
  viewMode?: 'week' | 'month';
  onRefresh?: () => void;
}

export const DashboardKpi = ({ kpiMetrics, viewMode = 'month', onRefresh }: DashboardKpiProps) => {
  const theme = useTheme();

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  const productionMetrics = kpiMetrics.productionMetricsByStage;
  const isWeekView = viewMode === 'week';

  // Material Design cohesive palette for production stages
  // Saw: Blue (#1976d2) - primary theme color, represents cutting/manufacturing
  // Weld: Cyan (#00acc1) - complements blue, represents joining/assembly
  // Paint: Amber (#ff9800) - warm finish color, represents finishing
  const sawColor = '#1976d2';
  const weldColor = '#00acc1';
  const paintColor = '#ff9800';

  // Prepare pie chart data - memoized to prevent unnecessary re-renders
  const pieData = useMemo(() => {
    return [
      { name: 'Saw', value: productionMetrics.sawFeet, color: sawColor },
      { name: 'Weld', value: productionMetrics.weldFeet, color: weldColor },
      { name: 'Paint', value: productionMetrics.paintFeet, color: paintColor },
    ].filter(item => item.value > 0);
  }, [productionMetrics.sawFeet, productionMetrics.weldFeet, productionMetrics.paintFeet]);

  // Memoize label function to prevent label flicker
  const renderLabel = useMemo(
    () =>
      ({ name, percent, value }: { name?: string; percent?: number; value: number }) =>
        `${name ?? 'Unknown'}: ${((percent ?? 0) * 100).toFixed(1)}% (${value.toLocaleString(
          'en-US',
          {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }
        )} ft)`,
    []
  );

  // Prepare bar chart data based on view mode
  const barData = useMemo(() => {
    if (
      isWeekView &&
      productionMetrics.dailyBreakdown &&
      productionMetrics.dailyBreakdown.length > 0
    ) {
      // Week view: Show daily breakdown (Sunday-Saturday)
      // Ensure we have all 7 days - map all entries from dailyBreakdown
      const mappedData = productionMetrics.dailyBreakdown.map(day => ({
        day: day.dayName.substring(0, 3), // Short day name (Sun, Mon, Tue, etc.)
        saw: day.sawFeet,
        weld: day.weldFeet,
        paint: day.paintFeet,
      }));

      return mappedData;
    } else {
      // Month view: Show statuses
      return [
        { status: 'Saw', feet: productionMetrics.sawFeet },
        { status: 'Weld', feet: productionMetrics.weldFeet },
        { status: 'Paint', feet: productionMetrics.paintFeet },
      ].filter(item => item.feet > 0);
    }
  }, [
    isWeekView,
    productionMetrics.dailyBreakdown,
    productionMetrics.sawFeet,
    productionMetrics.weldFeet,
    productionMetrics.paintFeet,
  ]);

  const totalFeet =
    productionMetrics.sawFeet + productionMetrics.weldFeet + productionMetrics.paintFeet;

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {/* Production Figures */}
      <Grid item xs={12} md={3}>
        <ProductionFigureCard
          title="Saw"
          value={productionMetrics.sawFeet}
          total={totalFeet}
          color={sawColor}
          onRefresh={handleRefresh}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <ProductionFigureCard
          title="Weld"
          value={productionMetrics.weldFeet}
          total={totalFeet}
          color={weldColor}
          onRefresh={handleRefresh}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <ProductionFigureCard
          title="Paint"
          value={productionMetrics.paintFeet}
          total={totalFeet}
          color={paintColor}
          onRefresh={handleRefresh}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <ProductionFigureCard
          title="Total"
          value={totalFeet}
          total={totalFeet}
          color={theme.palette.text.primary}
          onRefresh={handleRefresh}
          showPercentage={false}
        />
      </Grid>

      {/* Pie Chart */}
      <Grid item xs={12} md={6}>
        <Card elevation={3} sx={{ height: '100%' }}>
          <CardContent>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography variant="h6">Production by Stage</Typography>
              <Tooltip title="Refresh data">
                <IconButton size="small" onClick={handleRefresh}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderLabel}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    isAnimationActive={false}
                  >
                    {pieData.map(entry => (
                      <Cell key={`cell-${entry.name}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(value: number) => [
                      `${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ft`,
                      'Feet',
                    ]}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box
                sx={{
                  height: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                }}
              >
                <Typography variant="body2">No production data available</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Bar Chart - Daily breakdown for week view, status breakdown for month view */}
      <Grid item xs={12} md={6}>
        <Card elevation={3} sx={{ height: '100%' }}>
          <CardContent>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography variant="h6">
                {isWeekView ? 'Production by Day (Sunday - Saturday)' : 'Production by Stage'}
              </Typography>
              <Tooltip title="Refresh data">
                <IconButton size="small" onClick={handleRefresh}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {isWeekView ? (
              // Week view: Show grouped bar chart with daily breakdown
              barData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                      dataKey="day"
                      tick={{ fontSize: 12 }}
                      stroke={theme.palette.text.secondary}
                      type="category"
                      allowDuplicatedCategory={false}
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      stroke={theme.palette.text.secondary}
                      label={{ value: 'Feet', angle: -90, position: 'insideLeft' }}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: theme.shape.borderRadius,
                      }}
                      formatter={(value: number) => [
                        `${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ft`,
                        'Feet',
                      ]}
                    />
                    <Legend />
                    <Bar dataKey="saw" fill={sawColor} name="Saw" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="weld" fill={weldColor} name="Weld" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="paint" fill={paintColor} name="Paint" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    height: 300,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                  }}
                >
                  <Typography variant="body2">No production data available</Typography>
                </Box>
              )
            ) : // Month view: Show status breakdown
            barData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis
                    dataKey="status"
                    tick={{ fontSize: 12 }}
                    stroke={theme.palette.text.secondary}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    stroke={theme.palette.text.secondary}
                    label={{ value: 'Feet', angle: -90, position: 'insideLeft' }}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: theme.shape.borderRadius,
                    }}
                    formatter={(value: number) => [
                      `${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ft`,
                      'Feet',
                    ]}
                  />
                  <Bar dataKey="feet" radius={[4, 4, 0, 0]}>
                    {barData.map((entry, index) => {
                      let color = sawColor;
                      if ('status' in entry && entry.status === 'Weld') color = weldColor;
                      if ('status' in entry && entry.status === 'Paint') color = paintColor;
                      return <Cell key={`cell-${index}`} fill={color} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box
                sx={{
                  height: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                }}
              >
                <Typography variant="body2">No production data available</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

interface ProductionFigureCardProps {
  title: string;
  value: number;
  total: number;
  color: string;
  onRefresh: () => void;
  showPercentage?: boolean;
}

const ProductionFigureCard = ({
  title,
  value,
  total,
  color,
  onRefresh,
  showPercentage = true,
}: ProductionFigureCardProps) => {
  const theme = useTheme();
  const formattedValue = useMemo(() => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }, [value]);

  const percentage = useMemo(() => {
    if (!showPercentage || total === 0) return null;
    return ((value / total) * 100).toFixed(1);
  }, [value, total, showPercentage]);

  return (
    <Card
      elevation={3}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}
        >
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ fontSize: '0.875rem', fontWeight: 500 }}
          >
            {title}
          </Typography>
          <Tooltip title="Refresh data">
            <IconButton size="small" onClick={onRefresh} sx={{ ml: 1 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: color,
              lineHeight: 1,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            {formattedValue}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
            ft
          </Typography>
        </Box>
        {percentage !== null && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {percentage}% of total
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
